import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_API_URL || 'https://spotd-production-e50f.up.railway.app';

// Live job count rounded down to the hundred ("8,100+"); the fallback is what the
// prerendered HTML shows before the API answers.
const fmtJobCount = n => `${(Math.floor(n / 100) * 100).toLocaleString('en-US')}+`;

const P = '#635BFF'; // purple
const N = '#0A2540'; // navy
const BG = '#F7F8FA';
const BD = '#E3E8EF';
const G = '#10B981'; // green (recruiter)
const B = '#1D4ED8'; // brand blue
const Y = '#FBBF24'; // brand yellow

const CSS = `
  .ld-root{background:#fff;color:${N};font-family:'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;overflow-x:hidden}
  .ld-hero-bg{background:linear-gradient(135deg,#EFF6FF 0%,#F5F3FF 50%,#FFFBEB 100%)}
  .ld-section-blue{background:linear-gradient(135deg,#EFF6FF 0%,#F0F9FF 100%)}
  .ld-card-blue{border-top:3px solid ${B}!important}
  .ld-card-yellow{border-top:3px solid ${Y}!important}
  .ld-card-green{border-top:3px solid ${G}!important}
  .ld-stat-num{background:linear-gradient(135deg,${B},${P});-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
  @keyframes ld-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
  @keyframes ld-pulse{0%,100%{box-shadow:0 0 0 0 rgba(99,91,255,.3)}70%{box-shadow:0 0 0 12px rgba(99,91,255,0)}}
  @keyframes ld-slide{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  @keyframes ld-badge{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
  @keyframes ld-swipe{0%{transform:rotate(0) translateX(0);opacity:1}100%{transform:rotate(12deg) translateX(120px);opacity:0}}
  @keyframes ld-appear{0%{opacity:0;transform:scale(.94) translateY(8px)}100%{opacity:1;transform:scale(1) translateY(0)}}
  .ld-float{animation:ld-float 4s ease-in-out infinite}
  .ld-pulse{animation:ld-pulse 2.4s ease-in-out infinite}
  .ld-badge-a{animation:ld-badge 2.2s ease-in-out infinite}
  .ld-btn{transition:all .2s cubic-bezier(.25,.46,.45,.94);cursor:pointer;border:none}
  .ld-btn:hover{opacity:.9;transform:translateY(-1px)}
  .ld-card{transition:all .22s cubic-bezier(.25,.46,.45,.94)}
  .ld-card:hover{box-shadow:0 12px 40px rgba(10,37,64,.1)!important;transform:translateY(-2px)}
  .ld-nav-link{color:#64748B;font-size:.85rem;font-weight:500;text-decoration:none;padding:0 14px;transition:color .15s}
  .ld-nav-link:hover{color:${P}}
`;

function SwipeCard({ job, style = {} }) {
  return (
    <div style={{ background: '#fff', borderRadius: 18, padding: '20px', boxShadow: '0 4px 28px rgba(10,37,64,.1)', border: `1px solid ${BD}`, width: 280, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: `${job.color}12`, border: `1px solid ${job.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: job.color, fontSize: '1rem', fontFamily: 'Bricolage Grotesque, sans-serif' }}>
          {job.co[0]}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: '.88rem', color: N }}>{job.co}</div>
          <div style={{ fontSize: '.72rem', color: '#64748B' }}>{job.loc}</div>
        </div>
        <div className="ld-badge-a" style={{ marginLeft: 'auto', background: `${job.color}10`, color: job.color, fontSize: '.7rem', fontWeight: 700, padding: '4px 11px', borderRadius: 980, border: `1px solid ${job.color}20` }}>
          {job.score}% match
        </div>
      </div>
      <div style={{ fontWeight: 700, fontSize: '1.05rem', color: N, marginBottom: 5, letterSpacing: '-.02em' }}>{job.title}</div>
      <div style={{ fontSize: '.78rem', color: '#64748B', marginBottom: 14 }}>{job.type} · {job.sal}</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {job.tags.map(t => (
          <span key={t} style={{ background: BG, color: '#64748B', fontSize: '.68rem', fontWeight: 600, padding: '4px 10px', borderRadius: 980, border: `1px solid ${BD}` }}>{t}</span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, background: BG, borderRadius: 980, padding: '10px', textAlign: 'center', fontSize: '1.1rem', border: `1px solid ${BD}` }}>✕</div>
        <div style={{ flex: 3, background: P, borderRadius: 980, padding: '10px', textAlign: 'center', color: '#fff', fontSize: '.8rem', fontWeight: 600 }}>Apply Now</div>
      </div>
    </div>
  );
}

const DEMO_JOBS = [
  { co: 'Stripe',  loc: 'San Francisco', title: 'Senior Engineer',   type: 'Full-time', sal: '$170–190k', score: 94, color: P,        tags: ['React', 'Node.js', 'Remote OK'] },
  { co: 'Notion',  loc: 'New York',      title: 'Product Designer',  type: 'Full-time', sal: '$130–150k', score: 87, color: '#7C3AED', tags: ['Figma', 'UX', 'Hybrid'] },
  { co: 'Airbnb',  loc: 'Seattle',       title: 'Data Scientist',    type: 'Full-time', sal: '$155–175k', score: 81, color: '#EF4444', tags: ['Python', 'ML', 'On-site'] },
];

const FEATURES = [
  { icon: '🤖', title: 'AI Match Score', desc: 'Our AI reads your full profile and scores each job by real fit — skills, salary, experience, culture. Not just keywords.', color: P },
  { icon: '👆', title: 'Swipe to Apply', desc: 'No forms. No cover letters. Browse your matched jobs like a feed and swipe right on the ones you want. Done in seconds.', color: '#7C3AED' },
  { icon: '📩', title: 'Inbox, Not Outbox', desc: "Stop mass-applying. Once you swipe right, matched recruiters come to you — you're always in control of who reaches out.", color: G },
];

const STEPS = [
  { n: '01', title: 'Build your profile', desc: 'Add your skills, experience level, and salary range. Takes 2 minutes. Our AI fills in the details.' },
  { n: '02', title: 'Get matched instantly', desc: 'Our algorithm surfaces jobs that fit your profile — filtered by role, location, remote preference and salary.' },
  { n: '03', title: 'Swipe on what fits', desc: "See your AI match score on every job. Swipe right on the ones you like. It's that simple." },
  { n: '04', title: 'Recruiters come to you', desc: "Companies with open roles get notified when you match. You only hear from real opportunities." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [cardIdx, setCardIdx] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const swipeTimer = useRef(null);
  const [liveJobs, setLiveJobs] = useState('8,000+');

  useEffect(() => {
    fetch(`${API}/api/public/jobs/insights/`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.insights && d.insights.total) setLiveJobs(fmtJobCount(d.insights.total)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'ld-styles';
    style.textContent = CSS;
    document.head.appendChild(style);

    const root = document.getElementById('root');
    const body = document.body;
    const prevMaxWidth = root.style.maxWidth;
    const prevHeight = root.style.height;
    const prevOverflow = body.style.overflow;
    root.style.maxWidth = 'none';
    root.style.height = 'auto';
    body.style.overflow = 'auto';

    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);

    swipeTimer.current = setInterval(() => {
      setSwiping(true);
      setTimeout(() => { setCardIdx(i => (i + 1) % DEMO_JOBS.length); setSwiping(false); }, 600);
    }, 3200);

    return () => {
      document.getElementById('ld-styles')?.remove();
      root.style.maxWidth = prevMaxWidth;
      root.style.height = prevHeight;
      body.style.overflow = prevOverflow;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      clearInterval(swipeTimer.current);
    };
  }, []);

  const job = DEMO_JOBS[cardIdx];

  return (
    <div className="ld-root">
      <Helmet>
        <title>SpotdHire — AI-Matched Tech Jobs, No Cover Letters</title>
        <meta name="description" content="SpotdHire uses AI to match you with tech jobs that fit your skills and salary. No cover letters. Apply in 2 minutes. Let recruiters come to you." />
        <link rel="canonical" href="https://www.spotdhire.com/" />
        <meta property="og:title"       content="SpotdHire — AI-Matched Tech Jobs, No Cover Letters" />
        <meta property="og:description" content="AI job matching for tech. No cover letters. Your profile gets you matches — apply in 2 minutes." />
        <meta property="og:url"         content="https://www.spotdhire.com/" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="SpotdHire" />
        <meta property="og:image"       content="https://www.spotdhire.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt"   content="SpotdHire — AI-Matched Tech Jobs" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content="SpotdHire — AI-Matched Tech Jobs, No Cover Letters" />
        <meta name="twitter:description" content="AI job matching for tech. No cover letters. Your profile gets you matches — apply in 2 minutes." />
        <meta name="twitter:image"       content="https://www.spotdhire.com/og-image.png" />
        <meta name="twitter:image:alt"  content="SpotdHire — AI-Matched Tech Jobs" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.spotdhire.com/#organization',
          name: 'SpotdHire',
          url: 'https://www.spotdhire.com',
          logo: 'https://www.spotdhire.com/icons/icon-512.png',
          description: 'AI-powered job matching platform. No cover letters. Apply in 2 minutes.',
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'SpotdHire',
          url: 'https://www.spotdhire.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: { '@type': 'EntryPoint', urlTemplate: 'https://www.spotdhire.com/jobs?q={search_term_string}' },
            'query-input': 'required name=search_term_string',
          },
        })}</script>
      </Helmet>

      {/* ══ NAV ══ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 300,
        background: scrolled ? 'rgba(255,255,255,.92)' : 'rgba(255,255,255,.75)',
        borderBottom: `1px solid ${scrolled ? BD : 'rgba(227,232,239,.6)'}`,
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        transition: 'all .3s',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '0 20px' : '0 44px', height: 76, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <img src="/logo.svg" alt="SpotdHire" width="120" height="36" style={{ height: 36 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 8 }}>
            {!isMobile && <a href="#how-it-works" className="ld-nav-link" style={{fontSize:'.92rem',fontWeight:600}}>How it works</a>}
            {!isMobile && <a href="#recruiters" className="ld-nav-link" style={{fontSize:'.92rem',fontWeight:600}}>For recruiters</a>}
            {!isMobile && (
              <button className="ld-btn" onClick={() => navigate('/login')}
                style={{ background: Y, color: '#78350F', borderRadius: 980, padding: '9px 20px', fontSize: '.88rem', fontWeight: 700, marginLeft: 4 }}>
                Sign in
              </button>
            )}
            <button className="ld-btn" onClick={() => navigate('/login')}
              style={{ display: isMobile ? 'block' : 'none', background: Y, color: '#78350F', border: 'none', borderRadius: 980, padding: '8px 16px', fontSize: '.85rem', fontWeight: 700 }}>
              Sign in
            </button>
            {!isMobile && (
              <button className="ld-btn" onClick={() => navigate('/register')}
                style={{ background: `linear-gradient(135deg,${B},${P})`, color: '#fff', borderRadius: 980, padding: '11px 26px', fontSize: '.88rem', fontWeight: 700, boxShadow: `0 2px 14px ${B}40`, marginLeft: 4 }}>
                Get started free
              </button>
            )}
          </div>
        </div>
      </nav>

      <main>
      {/* ══ HERO ══ */}
      <section className="ld-hero-bg" style={{ borderBottom: `1px solid ${BD}` }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '100px 20px 60px' : '130px 44px 100px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 460px', gap: isMobile ? 0 : 80, alignItems: 'center' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `${Y}18`, border: `1px solid ${Y}50`, borderRadius: 980, padding: '7px 18px', marginBottom: 28, fontSize: '.78rem', color: '#92400E', fontWeight: 700 }}>
            <span style={{ fontSize: '1rem' }}>⚡</span>
            {liveJobs} live jobs — free for job seekers
          </div>

          <h1 style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,sans-serif", fontSize: 'clamp(2.6rem,4.5vw,3.8rem)', fontWeight: 900, lineHeight: 1.06, marginBottom: 22, letterSpacing: '-.04em', color: N }}>
            Your next job is<br />
            <span style={{ color: B }}>one swipe</span> <span style={{ color: Y }}>away.</span>
          </h1>

          <p style={{ fontSize: '1.05rem', color: '#64748B', lineHeight: 1.8, marginBottom: 40, maxWidth: 480 }}>
            Stop filling out forms. Swipe through real jobs matched to your skills and salary, apply in 2 minutes, and let recruiters come to you.
          </p>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginBottom: 20 }}>
            <button className="ld-btn" onClick={() => navigate('/jobs')}
              style={{ background: `linear-gradient(135deg,${B},${P})`, color: '#fff', borderRadius: 980, padding: '15px 32px', fontSize: '.97rem', fontWeight: 700, boxShadow: `0 4px 20px ${B}40` }}>
              Browse jobs — no signup →
            </button>
            <button className="ld-btn" onClick={() => navigate('/register?role=candidate')}
              style={{ background: '#fff', color: N, borderRadius: 980, padding: '15px 28px', fontSize: '.97rem', fontWeight: 600, border: `1.5px solid ${BD}` }}>
              Create my free profile
            </button>
          </div>
          <div style={{ fontSize: '.77rem', color: '#64748B', display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 22 }}>
            <span style={{ color: G, fontWeight: 600 }}>✓ 100% free for job seekers</span>
            <span>✓ No cover letters</span>
            <span>✓ Apply in 2 minutes</span>
          </div>
          <a href="#recruiters" style={{ fontSize: '.85rem', color: B, fontWeight: 700, textDecoration: 'none' }}>
            Hiring? Post a job for free →
          </a>
        </div>

        {!isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {/* Phone frame */}
            <div style={{
              width: 290, flexShrink: 0,
              background: '#111',
              borderRadius: 46,
              padding: '14px 9px 10px',
              boxShadow: '0 32px 80px rgba(10,37,64,.22), 0 0 0 1px rgba(255,255,255,.07) inset, 0 0 0 6px #1a1a1a',
              position: 'relative',
            }}>
              {/* Dynamic island */}
              <div style={{
                position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)',
                width: 88, height: 24, background: '#111',
                borderRadius: 980, zIndex: 10,
              }} />
              {/* Screen */}
              <div style={{
                width: '100%',
                background: 'linear-gradient(160deg,#EFF6FF 0%,#F5F3FF 100%)',
                borderRadius: 38,
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                minHeight: 560,
              }}>
                {/* App header */}
                <div style={{ padding: '38px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: '.78rem', letterSpacing: '-.01em' }}>
                    <span style={{ color: B }}>Spotd</span><span style={{ color: Y }}>Hire</span>
                  </div>
                  <div style={{ fontSize: '.62rem', color: '#64748B', fontWeight: 700 }}>9:41</div>
                </div>
                {/* Card: one at a time (stacked ghosts overlapped and looked broken) */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 14, minHeight: 460 }}>
                  <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#64748B', letterSpacing: '.04em', textTransform: 'uppercase' }}>Matched for you</div>
                  <div key={cardIdx} style={{ position: 'relative', animation: swiping ? 'ld-swipe .5s ease-in forwards' : 'ld-appear .35s ease-out' }}>
                    <SwipeCard job={job} style={{ width: 246, padding: 14 }} />
                    {swiping && (
                      <div style={{
                        position: 'absolute', top: -10, right: -6,
                        background: G, color: '#fff', fontWeight: 900, fontSize: '.72rem',
                        padding: '4px 10px', borderRadius: 7,
                        transform: 'rotate(14deg)', pointerEvents: 'none',
                      }}>APPLIED ✓</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${BD}`, borderRadius: 14, padding: '9px 12px', width: 222, boxShadow: '0 2px 10px rgba(10,37,64,.06)' }}>
                    <span style={{ fontSize: '1rem' }}>👀</span>
                    <span style={{ fontSize: '.68rem', color: N, fontWeight: 600, lineHeight: 1.35 }}>A recruiter at {DEMO_JOBS[(cardIdx + 1) % DEMO_JOBS.length].co} viewed your profile</span>
                  </div>
                </div>
                {/* Swipe hint */}
                <div style={{ padding: '8px 14px 18px', textAlign: 'center', flexShrink: 0, fontSize: '.62rem', color: '#94A3B8', letterSpacing: '.03em' }}>
                  👈 skip &nbsp;·&nbsp; apply 👉
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </section>

      {/* ══ TRUST BAR ══ */}
      <div style={{ borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}`, background: BG, padding: isMobile ? '22px 16px' : '32px 44px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-around' : 'space-between', gap: isMobile ? 16 : 32, flexWrap: 'wrap' }}>
          <div style={{ fontSize: '.72rem', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', flexShrink: 0 }}>By the numbers</div>
          {[
            { n: liveJobs, l: 'Live jobs' },
            { n: '2 min',   l: 'Profile setup' },
            { n: '$0',      l: 'For job seekers' },
            { n: '24h',     l: 'First matches' },
          ].map(s => (
            <div key={s.n} style={{ textAlign: 'center' }}>
              <div className="ld-stat-num" style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontSize: '1.55rem', fontWeight: 900, lineHeight: 1, letterSpacing: '-.03em' }}>{s.n}</div>
              <div style={{ fontSize: '.72rem', color: '#64748B', marginTop: 5 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ FEATURES ══ */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '60px 20px' : '100px 44px' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 60 }}>
          <div style={{ display:'inline-flex',alignItems:'center',gap:8,background:`${Y}18`,border:`1px solid ${Y}50`,borderRadius:980,padding:'5px 16px',marginBottom:14,fontSize:'.78rem',color:'#92400E',fontWeight:700 }}>
            ⚡ Why SpotdHire
          </div>
          <h2 style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontSize: isMobile ? '1.9rem' : '2.5rem', fontWeight: 900, color: N, letterSpacing: '-.04em', lineHeight: 1.1 }}>
            Job search, <span style={{color:B}}>completely reimagined</span>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 12 : 20 }}>
          {FEATURES.map((f, i) => (
            <div key={i} className={`ld-card ${['ld-card-blue','ld-card-yellow','ld-card-green'][i]}`} style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 18, padding: '32px 28px', boxShadow: '0 2px 12px rgba(10,37,64,.05)' }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${f.color}0d`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: 22 }}>{f.icon}</div>
              <h3 style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontWeight: 800, fontSize: '1.05rem', color: N, marginBottom: 10, letterSpacing: '-.02em' }}>{f.title}</h3>
              <p style={{ fontSize: '.88rem', color: '#64748B', lineHeight: 1.75, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section id="how-it-works" className="ld-section-blue" style={{ borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}`, padding: isMobile ? '60px 20px' : '100px 44px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 60 }}>
            <div style={{ display:'inline-flex',alignItems:'center',gap:8,background:`${B}10`,border:`1px solid ${B}25`,borderRadius:980,padding:'5px 16px',marginBottom:14,fontSize:'.78rem',color:B,fontWeight:700 }}>
              🗺 The process
            </div>
            <h2 style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontSize: isMobile ? '1.9rem' : '2.5rem', fontWeight: 900, color: N, letterSpacing: '-.04em', lineHeight: 1.1 }}>
              From profile to offer <span style={{color:B}}>in days</span>
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 24 : 0, position: 'relative' }}>
            {!isMobile && <div style={{ position: 'absolute', top: 27, left: '12.5%', right: '12.5%', height: 1, background: `linear-gradient(90deg,${P}40,${P}40)`, zIndex: 0 }} />}
            {STEPS.map((s, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '0 24px', position: 'relative', zIndex: 1 }}>
                <div style={{ width: 54, height: 54, borderRadius: '50%', background: i%2===0?B:Y, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px', boxShadow: `0 4px 16px ${i%2===0?B:Y}40` }}>
                  <span style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontWeight: 900, fontSize: '1rem', color: i%2===0?'#fff':N }}>{s.n}</span>
                </div>
                <h3 style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontWeight: 800, fontSize: '1rem', color: N, marginBottom: 10, letterSpacing: '-.02em' }}>{s.title}</h3>
                <p style={{ fontSize: '.83rem', color: '#64748B', lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ RECRUITERS ══ */}
      <section id="recruiters" style={{ background:`linear-gradient(135deg,#0F2952 0%,#1D4ED8 100%)`, padding: isMobile ? '60px 20px' : '100px 44px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 36 : 80, alignItems: 'center' }}>
          <div>
            <div style={{ display:'inline-flex',alignItems:'center',gap:8,background:'rgba(251,191,36,.18)',border:'1px solid rgba(251,191,36,.4)',borderRadius:980,padding:'5px 16px',marginBottom:18,fontSize:'.78rem',color:Y,fontWeight:700 }}>
              🏢 For recruiters
            </div>
            <h2 style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontSize: isMobile ? '1.9rem' : '2.5rem', fontWeight: 900, color: '#fff', marginBottom: 18, lineHeight: 1.1, letterSpacing: '-.04em' }}>
              Stop reading résumés.<br />
              <span style={{ color: Y }}>Let AI do it.</span>
            </h2>
            <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,.72)', lineHeight: 1.8, marginBottom: 32 }}>
              Post a role and let our AI rank candidates by real fit. No more wading through 300 applications. Only see people who actually match.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="ld-btn" onClick={() => navigate('/register?role=recruiter')}
                style={{ background: Y, color: N, borderRadius: 980, padding: '14px 30px', fontSize: '.97rem', fontWeight: 800, boxShadow: `0 4px 20px rgba(0,0,0,.25)` }}>
                Start hiring free →
              </button>
              <div style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.6)' }}>
                <span style={{ fontWeight: 700, color: '#fff' }}>3 free credits</span> to discover candidates
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { icon: '🤖', title: 'AI pre-screening',        desc: 'Our AI evaluates every candidate against your role criteria — so you only see qualified matches.' },
              { icon: '📊', title: 'Ranked by fit score',     desc: 'Candidates arrive sorted by match score. Highest fit first, always.' },
              { icon: '💳', title: 'Pay only for matches',    desc: 'No subscriptions. Buy credit packs and use them when you need them.' },
              { icon: '⚡', title: 'First candidates in 24h', desc: 'Post today, see your first matches tomorrow. No waiting, no job board queues.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="ld-card" style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 14, padding: '16px 18px', display: 'flex', gap: 14 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>{icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '.9rem', color: '#fff', marginBottom: 3, letterSpacing: '-.01em' }}>{title}</div>
                  <div style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.6)', lineHeight: 1.65 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section style={{ background: N, padding: isMobile ? '64px 20px' : '110px 44px', textAlign: 'center' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Bricolage Grotesque, sans-serif', fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 900, color: '#fff', marginBottom: 16, lineHeight: 1.1, letterSpacing: '-.04em' }}>
            Your next job is already<br />waiting for you.
          </h2>
          <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,.6)', marginBottom: 40, lineHeight: 1.75 }}>
            Join SpotdHire and let AI match you to the right opportunities — no résumé spam, no ghosting, no waiting.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="ld-btn" onClick={() => navigate('/register')}
              style={{ background: P, color: '#fff', borderRadius: 980, padding: '16px 38px', fontSize: '1rem', fontWeight: 700, boxShadow: `0 4px 24px ${P}50` }}>
              Get started free — 2 min
            </button>
            <button className="ld-btn" onClick={() => navigate('/login')}
              style={{ background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.85)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 980, padding: '16px 30px', fontSize: '1rem', fontWeight: 600 }}>
              Sign in →
            </button>
          </div>
          <div style={{ fontSize: '.77rem', color: 'rgba(255,255,255,.3)', marginTop: 22, display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
            <span>✓ Free forever for candidates</span>
            <span>✓ Simple & fast setup</span>
            <span>✓ Cancel anytime</span>
          </div>
        </div>
      </section>

      </main>

      {/* ══ FOOTER ══ */}
      <footer style={{ background: '#060F1E', borderTop: '1px solid rgba(255,255,255,.06)', padding: isMobile ? '48px 20px 32px' : '64px 44px 40px', color: '#64748B' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Nav columns */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '32px 20px' : '32px', marginBottom: 48 }}>
            <div>
              <div style={{ color: '#94A3B8', fontWeight: 700, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14 }}>By Industry</div>
              {/* Only live hub pages: role/extra-city slugs 301-redirect (backend/config/programmaticJobPages.js),
                  and this homepage is our strongest page for passing crawl priority to the hubs. */}
              {[['tech','Tech'],['finance','Finance'],['healthcare','Healthcare'],['marketing','Marketing'],['legal','Legal'],['consulting','Consulting'],['creative','Creative'],['trades','Trades']].map(([slug, label]) => (
                <a key={slug} href={`/jobs/${slug}`} style={{ display: 'block', color: '#fff', textDecoration: 'none', fontSize: '.9rem', marginBottom: 8, transition: 'color .15s' }}
                  onMouseEnter={e => e.target.style.color='#94A3B8'} onMouseLeave={e => e.target.style.color='#fff'}>{label}</a>
              ))}
            </div>
            <div>
              <div style={{ color: '#94A3B8', fontWeight: 700, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14 }}>By City</div>
              {[['new-york','New York'],['san-francisco','San Francisco'],['los-angeles','Los Angeles'],['chicago','Chicago'],['washington-dc','Washington DC'],['dallas','Dallas']].map(([slug, label]) => (
                <a key={slug} href={`/jobs/${slug}`} style={{ display: 'block', color: '#fff', textDecoration: 'none', fontSize: '.9rem', marginBottom: 8, transition: 'color .15s' }}
                  onMouseEnter={e => e.target.style.color='#94A3B8'} onMouseLeave={e => e.target.style.color='#fff'}>{label}</a>
              ))}
            </div>
            <div>
              <div style={{ color: '#94A3B8', fontWeight: 700, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14 }}>Job Type</div>
              {[['remote','Remote Jobs'],['full-time','Full-Time']].map(([slug, label]) => (
                <a key={slug} href={`/jobs/${slug}`} style={{ display: 'block', color: '#fff', textDecoration: 'none', fontSize: '.9rem', marginBottom: 8, transition: 'color .15s' }}
                  onMouseEnter={e => e.target.style.color='#94A3B8'} onMouseLeave={e => e.target.style.color='#fff'}>{label}</a>
              ))}
            </div>
            <div>
              <div style={{ color: '#94A3B8', fontWeight: 700, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14 }}>SpotdHire</div>
              {[['/about','About'],['https://www.spotdhire.com/jobs','Browse Jobs'],['/community','Community'],['/help','Help & FAQ'],['/terms','Terms of Service'],['/privacy','Privacy Policy']].map(([href, label]) => (
                <a key={href} href={href} style={{ display: 'block', color: '#fff', textDecoration: 'none', fontSize: '.9rem', marginBottom: 8, transition: 'color .15s' }}
                  onMouseEnter={e => e.target.style.color='#94A3B8'} onMouseLeave={e => e.target.style.color='#fff'}>{label}</a>
              ))}
            </div>
          </div>
          {/* Bottom bar */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', paddingTop: 28, display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <img src="/logo-white.svg" alt="SpotdHire" width="100" height="30" style={{ height: 30, marginBottom: 4 }} />
              <div style={{ fontSize: '.85rem', color: '#fff' }}>AI-powered job matching · United States</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <a href="https://fazier.com" target="_blank" rel="noopener">
                <img src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=launched&theme=light" width="120" height="26" alt="Launched on Fazier" style={{ display: 'block' }} />
              </a>
              <span style={{ color: '#fff', fontSize: '.9rem' }}>© 2026 SpotdHire</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
